import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  TablePagination,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TenantLedger from './TenantLedger';
import TenantTransferDialog from './TenantTransferDialog';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import axios from '../../api/client';
import EmptyState from '../EmptyState';
import { useCurrency } from '../../context/CurrencyContext';
import { toast } from 'react-toastify';
import ScrollTable from '../ScrollTable';

const TenantManagement = () => {
  const { formatMoney } = useCurrency();
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [open, setOpen] = useState(false);
  const [allocationOpen, setAllocationOpen] = useState(false);
  const [addTenantOpen, setAddTenantOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isReassigning, setIsReassigning] = useState(false);
  const [viewLedger, setViewLedger] = useState(false);
  const [editContactOpen, setEditContactOpen] = useState(false);
  // The tenant being moved to another unit, or null. See TenantTransferDialog.
  const [transferTenant, setTransferTenant] = useState(null);
  const [search, setSearch] = useState('');
  const [filterProperty, setFilterProperty] = useState('');
  const [filterLandlord, setFilterLandlord] = useState('');
  const [landlords, setLandlords] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalTenants, setTotalTenants] = useState(0);
  const [contactData, setContactData] = useState({ id: '', name: '', email: '', phone: '', whatsappPhone: '' });
  // Two separate days per tenant. paymentDate is when rent falls DUE; billingCycleDay is
  // when their rent month STARTS (10th to 9th for someone who moved in on the 10th). They
  // differ because tenants ask to pay on the day they are paid, which is often after their
  // month has begun. Both default to 1 — the calendar month — unless someone sets them.
  const [formData, setFormData] = useState({
    propertyId: '',
    unitId: '',
    leaseStartDate: new Date().toISOString().split('T')[0],
    leaseEndDate: '',
    paymentDate: 1, // Day of month (1-31)
    billingCycleDay: 1,
  });
  const [allocationData, setAllocationData] = useState({
    propertyId: '',
    squareFeet: 0,
    monthlyRent: 0,
    deposit: 0,
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    description: '',
    paymentDate: 1, // Day of month (1-31)
    billingCycleDay: 1,
  });
  const [newTenantData, setNewTenantData] = useState({
    name: '',
    email: '',
    phone: '',
    whatsappPhone: '',
    paymentDate: 1, // Day of month (1-31)
    billingCycleDay: 1,
    assignUnit: false,
    propertyId: '',
    unitId: '',
    leaseStartDate: new Date().toISOString().split('T')[0],
    leaseEndDate: '',
    // Commercial allocation when assigning commercial property
    allocationSquareFeet: 0,
    allocationMonthlyRent: 0,
    allocationDeposit: 0,
  });

  useEffect(() => {
    fetchTenants();
    fetchProperties();
    fetchLandlords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Searched and paged on the SERVER.
  //
  // Filtering in the browser would only ever search the rows already fetched — the endpoint
  // returns one page — so looking for a tenant who happens to be on page 7 would report
  // that they do not exist. It also renders 500 rows to find one, which is what made this
  // table hard to move around in the first place.
  const fetchTenants = async (opts = {}) => {
    const targetPage = opts.page ?? page;
    const targetSize = opts.rowsPerPage ?? rowsPerPage;
    const targetSearch = opts.search ?? search;
    const targetProperty = opts.propertyId ?? filterProperty;
    const targetLandlord = opts.landlordId ?? filterLandlord;

    try {
      const response = await axios.get('/tenants', {
        params: {
          page: targetPage + 1,
          limit: targetSize,
          ...(targetSearch ? { search: targetSearch } : {}),
          ...(targetProperty ? { propertyId: targetProperty } : {}),
          ...(targetLandlord ? { landlordId: targetLandlord } : {})
        }
      });
      const body = response.data;
      if (Array.isArray(body)) {
        setTenants(body);
        setTotalTenants(body.length);
      } else {
        setTenants(body.data || []);
        setTotalTenants(body.total ?? (body.data || []).length);
      }
    } catch (error) {
      toast.error('Error fetching tenants');
    }
  };

  const fetchLandlords = async () => {
    try {
      const response = await axios.get('/landlords');
      const all = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setLandlords(all.filter((l) => l.isActive !== false));
    } catch (error) {
      setLandlords([]);
    }
  };

  // Debounced so a search runs when typing stops rather than once per keystroke — a
  // request per character would race, and the answer to "Ach" could land after "Achieng".
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      fetchTenants({ page: 0, search });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const applyFilter = (changes) => {
    setPage(0);
    fetchTenants({ page: 0, ...changes });
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
    fetchTenants({ page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const size = parseInt(event.target.value, 10);
    setRowsPerPage(size);
    setPage(0);
    fetchTenants({ page: 0, rowsPerPage: size });
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get('/properties?availableForAssignment=true');
      // Filter to only show available properties with active landlords
      setProperties(response.data.filter(p => 
        (p.status === 'available' || p.status === 'occupied') && 
        p.landlord && 
        p.landlord.isActive
      ));
    } catch (error) {
      toast.error('Error fetching properties');
    }
  };

  const fetchUnits = async (propertyId, showAll = false) => {
    if (!propertyId) {
      setUnits([]);
      return;
    }
    try {
      const property = properties.find(p => p._id === propertyId);
      if (property && property.propertyType !== 'commercial') {
        const response = await axios.get(`/units/property/${propertyId}`);
        // Show all units when reassigning, otherwise only available ones
        if (showAll) {
          setUnits(response.data);
        } else {
          setUnits(response.data.filter(u => u.status === 'available'));
        }
      } else {
        setUnits([]);
      }
    } catch (error) {
      toast.error('Error fetching units');
    }
  };

  const handleAssign = (tenant) => {
    setSelectedTenant(tenant);
    setIsReassigning(true);
    const propertyId = tenant.property?._id || tenant.unit?.property?._id || tenant.unit?.property || '';
    setFormData({
      propertyId: propertyId,
      unitId: tenant.unit?._id || tenant.unit || '',
      leaseStartDate: tenant.leaseStartDate
        ? new Date(tenant.leaseStartDate).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      leaseEndDate: tenant.leaseEndDate
        ? new Date(tenant.leaseEndDate).toISOString().split('T')[0]
        : '',
      paymentDate: tenant.paymentDate || 1,
      billingCycleDay: tenant.billingCycleDay || 1,
    });

    // Check property type to determine which dialog to show
    const property = properties.find(p => p._id === propertyId);
    if (property && property.propertyType === 'commercial') {
      setAllocationOpen(true);
      // Fetch existing allocation if any
      axios.get(`/commercial-allocations?tenant=${tenant._id}&property=${propertyId}`)
        .then(res => {
          if (res.data && res.data.length > 0) {
            const alloc = res.data[0];
            setAllocationData({
              propertyId: propertyId,
              squareFeet: alloc.squareFeet || 0,
              monthlyRent: alloc.monthlyRent || 0,
              deposit: alloc.deposit || 0,
              startDate: alloc.startDate 
                ? new Date(alloc.startDate).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0],
              endDate: alloc.endDate 
                ? new Date(alloc.endDate).toISOString().split('T')[0]
                : '',
              description: alloc.description || '',
            });
          } else {
            setAllocationData({
              propertyId: propertyId,
              squareFeet: 0,
              monthlyRent: 0,
              deposit: 0,
              startDate: new Date().toISOString().split('T')[0],
              endDate: '',
              description: '',
            });
          }
        })
        .catch(() => {
          setAllocationData({
            propertyId: propertyId,
            squareFeet: 0,
            monthlyRent: 0,
            deposit: 0,
            startDate: new Date().toISOString().split('T')[0],
            endDate: '',
            description: '',
          });
        });
    } else {
      setAllocationData({
        propertyId: '',
        squareFeet: 0,
        monthlyRent: 0,
        deposit: 0,
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        description: '',
        paymentDate: 1,
        billingCycleDay: tenant.billingCycleDay || 1,
      });
      setOpen(true);
      if (propertyId) {
        // When reassigning, show all units (including occupied ones)
        fetchUnits(propertyId, true);
      }
    }
  };

  const handleAddTenant = () => {
    setNewTenantData({
      name: '',
      email: '',
      password: '',
      phone: '',
      whatsappPhone: '',
      paymentDate: 1,
      billingCycleDay: 1,
      assignUnit: false,
      propertyId: '',
      unitId: '',
      leaseStartDate: new Date().toISOString().split('T')[0],
      leaseEndDate: '',
      allocationSquareFeet: 0,
      allocationMonthlyRent: 0,
      allocationDeposit: 0,
    });
    setAddTenantOpen(true);
  };

  // Contact details are otherwise only settable at creation time, which would leave
  // existing tenants unable to record a separate WhatsApp number.
  // Asks the server whether this tenant can go before offering to remove them, so staff
  // are not invited to press something that will be refused — and so the refusal explains
  // itself in terms of records rather than as an error.
  //
  // A tenant with invoices, payments or ledger entries is the counterparty to money that
  // moved. Deleting them would leave those records pointing at nothing; ending the tenancy
  // keeps the history.
  const handleDelete = async (tenant) => {
    try {
      const { data } = await axios.get(`/tenants/${tenant._id}/deletable`);
      if (!data.deletable) {
        const { invoices, payments, ledgerEntries } = data.history;
        toast.error(
          `${tenant.name} has ${invoices} invoice(s), ${payments} payment(s) and `
          + `${ledgerEntries} ledger entr(ies), so they cannot be deleted. `
          + 'End the tenancy instead — clear their unit and lease dates, which keeps the history.'
        );
        return;
      }
      if (!window.confirm(`Delete ${tenant.name}? They have no financial records. This cannot be undone.`)) return;
      const { data: result } = await axios.delete(`/tenants/${tenant._id}`);
      toast.success(result.message);
      fetchTenants();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not delete this tenant');
    }
  };

  // Dates arrive as ISO timestamps; the date inputs want YYYY-MM-DD.
  const asDay = (d) => (d ? new Date(d).toISOString().split('T')[0] : '');

  const handleEditContact = (tenant) => {
    setContactData({
      id: tenant._id,
      name: tenant.name || '',
      email: tenant.email || '',
      phone: tenant.phone || '',
      whatsappPhone: tenant.whatsappPhone || '',
      // Editable, but NOT the tenancy itself. Moving a tenant between properties or units
      // goes through Assign / Reassign, which also frees the old unit and occupies the new
      // one - doing it from here would leave the unit records disagreeing with the tenant
      // about who lives where.
      paymentDate: tenant.paymentDate || 1,
      billingCycleDay: tenant.billingCycleDay || 1,
      leaseStartDate: asDay(tenant.leaseStartDate),
      leaseEndDate: asDay(tenant.leaseEndDate),
      emergencyContactName: tenant.emergencyContactName || '',
      emergencyContactPhone: tenant.emergencyContactPhone || '',
      emergencyContactRelation: tenant.emergencyContactRelation || '',
    });
    setEditContactOpen(true);
  };

  const handleSaveContact = async () => {
    try {
      const day = Number(contactData.paymentDate);
      if (!Number.isInteger(day) || day < 1 || day > 31) {
        toast.error('Rent day must be a whole number between 1 and 31');
        return;
      }
      const cycleDay = Number(contactData.billingCycleDay);
      if (!Number.isInteger(cycleDay) || cycleDay < 1 || cycleDay > 31) {
        toast.error('Billing cycle day must be a whole number between 1 and 31');
        return;
      }
      if (contactData.leaseStartDate && contactData.leaseEndDate
        && contactData.leaseEndDate < contactData.leaseStartDate) {
        toast.error('Lease end date cannot be before the lease start date');
        return;
      }

      await axios.put(`/tenants/${contactData.id}`, {
        paymentDate: day,
        billingCycleDay: cycleDay,
        // Empty means "not recorded", which differs from an invalid date - sent as null so
        // clearing a lease end actually clears it rather than being ignored.
        leaseStartDate: contactData.leaseStartDate || null,
        leaseEndDate: contactData.leaseEndDate || null,
        emergencyContactName: contactData.emergencyContactName,
        emergencyContactPhone: contactData.emergencyContactPhone,
        emergencyContactRelation: contactData.emergencyContactRelation,
        name: contactData.name,
        email: contactData.email || null,
        phone: contactData.phone || null,
        whatsappPhone: contactData.whatsappPhone || null,
      });
      toast.success('Contact details updated');
      setEditContactOpen(false);
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update contact details');
    }
  };

  const handleCreateTenant = async () => {
    try {
      if (!newTenantData.name) {
        toast.error('Please provide tenant name');
        return;
      }

      // Create tenant (no user account)
      const response = await axios.post('/tenants', {
        name: newTenantData.name,
        email: newTenantData.email || undefined,
        phone: newTenantData.phone || undefined,
        whatsappPhone: newTenantData.whatsappPhone || undefined,
        paymentDate: newTenantData.paymentDate || 1,
        billingCycleDay: newTenantData.billingCycleDay || 1,
      });

      toast.success('Tenant created successfully');

      // If assignUnit is checked, assign property (residential unit or commercial allocation)
      if (newTenantData.assignUnit && newTenantData.propertyId) {
        const property = properties.find(p => p._id === newTenantData.propertyId);
        if (property?.propertyType === 'commercial') {
          if (newTenantData.allocationSquareFeet > 0) {
            await axios.post('/commercial-allocations', {
              tenant: response.data._id,
              property: newTenantData.propertyId,
              squareFeet: newTenantData.allocationSquareFeet,
              monthlyRent: newTenantData.allocationMonthlyRent,
              deposit: newTenantData.allocationDeposit || 0,
              startDate: newTenantData.leaseStartDate,
              endDate: newTenantData.leaseEndDate || undefined,
              paymentDate: newTenantData.paymentDate || 1,
              billingCycleDay: newTenantData.billingCycleDay || 1,
            });
            toast.success('Commercial allocation created for tenant');
          }
        } else if (newTenantData.unitId) {
          await axios.put(`/units/${newTenantData.unitId}/assign`, {
            tenantId: response.data._id,
            leaseStartDate: newTenantData.leaseStartDate,
            leaseEndDate: newTenantData.leaseEndDate || null,
            paymentDate: newTenantData.paymentDate || 1,
            billingCycleDay: newTenantData.billingCycleDay || 1,
          });
          toast.success('Unit assigned to tenant');
        }
      }

      setAddTenantOpen(false);
      fetchTenants();
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error creating tenant');
    }
  };

  const handlePropertyChange = (propertyId) => {
    setFormData({ ...formData, propertyId, unitId: '' });
    const property = properties.find(p => p._id === propertyId);
    setSelectedProperty(property);
    if (property?.propertyType === 'commercial') {
      setAllocationData(prev => ({ ...prev, propertyId }));
    }
    // When reassigning, show all units; otherwise only available
    fetchUnits(propertyId, isReassigning);
  };

  const handleNewTenantPropertyChange = (propertyId) => {
    setNewTenantData({ ...newTenantData, propertyId, unitId: '' });
    setSelectedProperty(properties.find(p => p._id === propertyId));
    fetchUnits(propertyId, false); // Only show available units for new tenant
  };

  const handleSubmit = async () => {
    try {
      if (!formData.propertyId) {
        toast.error('Please select a property');
        return;
      }

      const property = properties.find(p => p._id === formData.propertyId);
      
      if (property.propertyType === 'commercial') {
        // Handle commercial allocation
        if (!allocationData.squareFeet || allocationData.squareFeet <= 0) {
          toast.error('Square feet must be greater than 0');
          return;
        }
        
        // Check if allocation exists
        const existingAlloc = await axios.get(`/commercial-allocations?tenant=${selectedTenant._id}&property=${formData.propertyId}`);

        if (existingAlloc.data && existingAlloc.data.length > 0) {
          // Update existing allocation
          await axios.put(`/commercial-allocations/${existingAlloc.data[0]._id}`, {
            ...allocationData,
            tenant: selectedTenant._id,
            paymentDate: formData.paymentDate || allocationData.paymentDate || 1,
            billingCycleDay: allocationData.billingCycleDay || 1,
          });
          toast.success('Commercial allocation updated');
        } else {
          // Create new allocation
          await axios.post('/commercial-allocations', {
            ...allocationData,
            tenant: selectedTenant._id,
            property: formData.propertyId,
            paymentDate: formData.paymentDate || allocationData.paymentDate || 1,
            billingCycleDay: allocationData.billingCycleDay || 1,
          });
          toast.success('Commercial allocation created');
        }
        
        setAllocationOpen(false);
      } else {
        // Handle residential unit assignment
        if (!formData.unitId) {
          toast.error('Please select a unit');
          return;
        }
        
        await axios.put(`/units/${formData.unitId}/assign`, {
          tenantId: selectedTenant._id,
          leaseStartDate: formData.leaseStartDate,
          leaseEndDate: formData.leaseEndDate || null,
          paymentDate: formData.paymentDate || 1,
          billingCycleDay: formData.billingCycleDay || 1,
        });
        toast.success('Unit assigned to tenant');
        setOpen(false);
      }
      
      fetchTenants();
      fetchProperties();
      setIsReassigning(false);
    } catch (error) {
      // The server refuses to reassign a tenant who has a financial history, because a
      // reassignment leaves the month's rent, their arrears and their deposit where they
      // were. It names the process that handles those, so open it.
      if (error.response?.status === 409 && error.response?.data?.code === 'USE_TRANSFER') {
        toast.info(error.response.data.message);
        setOpen(false);
        setIsReassigning(false);
        setTransferTenant(selectedTenant);
        return;
      }
      toast.error(error.response?.data?.message || 'Error assigning property/unit');
      setIsReassigning(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">
          Tenant Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddTenant}
        >
          Add Tenant
        </Button>
      </Box>
      <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
        <TextField
          size="small"
          label="Search tenants"
          placeholder="Name, phone or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260, flex: 1 }}
          InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
        />
        <TextField
          size="small" select label="Landlord" value={filterLandlord}
          onChange={(e) => { setFilterLandlord(e.target.value); applyFilter({ landlordId: e.target.value }); }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All landlords</MenuItem>
          {landlords.map((l) => (
            <MenuItem key={l._id || l.id} value={l._id || l.id}>{l.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small" select label="Property" value={filterProperty}
          onChange={(e) => { setFilterProperty(e.target.value); applyFilter({ propertyId: e.target.value }); }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All properties</MenuItem>
          {properties.map((prop) => (
            <MenuItem key={prop._id || prop.id} value={prop._id || prop.id}>{prop.propertyName}</MenuItem>
          ))}
        </TextField>
        {(search || filterLandlord || filterProperty) && (
          <Button onClick={() => {
            setSearch(''); setFilterLandlord(''); setFilterProperty('');
            applyFilter({ search: '', landlordId: '', propertyId: '' });
          }}>Clear</Button>
        )}
      </Box>

      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell className="pin-left">Name</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Property/Unit</TableCell>
              <TableCell>Lease</TableCell>
              <TableCell className="pin-right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenants.map((tenant) => (
              <TableRow key={tenant._id}>
                <TableCell className="pin-left">
                  {tenant.name || 'N/A'}
                </TableCell>
                {/* Email, phone and WhatsApp were three columns of mostly-short values, and
                    three columns is three times the width to scroll past. Stacked, they
                    read as one fact about the person. A blank WhatsApp number means
                    notifications fall back to the phone. */}
                <TableCell>
                  <Typography variant="body2">{tenant.email || '\u2014'}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {tenant.phone || 'No phone'}
                    {tenant.whatsappPhone ? ` \u00b7 WhatsApp ${tenant.whatsappPhone}` : ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  {tenant.unit ? (
                    <>
                      {tenant.unit.property?.propertyName || tenant.property?.propertyName} - 
                      Unit {tenant.unit.unitNumber || tenant.unit._id || ''}
                    </>
                  ) : tenant.property ? (
                    <>
                      {tenant.property.propertyName}
                      {tenant.property.propertyType === 'commercial' && ' (Commercial)'}
                    </>
                  ) : (
                    'Not Assigned'
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {tenant.leaseStartDate
                      ? `From ${new Date(tenant.leaseStartDate).toLocaleDateString()}`
                      : 'No start date'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {tenant.paymentDate ? `Pays day ${tenant.paymentDate}` : 'Pay day not set'}
                    {tenant.billingCycleDay && tenant.billingCycleDay !== 1
                      ? ` · month runs from day ${tenant.billingCycleDay}`
                      : ''}
                  </Typography>
                </TableCell>
                <TableCell className="pin-right">
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      startIcon={<AssignmentIcon />}
                      onClick={() => handleAssign(tenant)}
                    >
                      {tenant.unit || tenant.property ? 'Change Assignment' : 'Assign'}
                    </Button>
                    {tenant.unit && (
                      <Button
                        size="small"
                        startIcon={<SwapHorizIcon />}
                        onClick={() => setTransferTenant(tenant)}
                        variant="outlined"
                      >
                        Transfer
                      </Button>
                    )}
                    <Button
                      size="small"
                      startIcon={<AccountBalanceIcon />}
                      onClick={() => {
                        setSelectedTenant(tenant);
                        setViewLedger(true);
                      }}
                      variant="outlined"
                    >
                      Ledger
                    </Button>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditContact(tenant)}
                      variant="outlined"
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDelete(tenant)}
                    >
                      Delete
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {tenants.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  {/* A search that found nothing is a different message from having no
                      tenants at all — the second reads as data loss when it is a filter. */}
                  {(search || filterLandlord || filterProperty) ? (
                    <EmptyState
                      title="No tenants match"
                      description="Try a different name, phone or email, or clear the filters."
                    />
                  ) : (
                    <EmptyState title="No tenants yet" description="Tenants appear here once they're assigned to a property or unit." />
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
      </ScrollTable>
      {/* This was missing entirely: the endpoint has always paged at 50, so on an agency
          with 500 tenants the other 450 were invisible with nothing on screen saying so. */}
      <TablePagination
        component="div"
        count={totalTenants}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
      />

      {/* Residential Unit Assignment Dialog */}
      <Dialog open={open} onClose={() => {
        setOpen(false);
        setIsReassigning(false);
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isReassigning ? 'Reassign Unit to Tenant' : 'Assign Unit to Tenant'}
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            options={properties}
            value={properties.find(p => p._id === formData.propertyId) || null}
            getOptionLabel={(p) => `${p.propertyName}${p.addressStreet ? ` - ${p.addressStreet}` : ''} (${p.propertyType})`}
            onChange={(_, val) => handlePropertyChange(val?._id || '')}
            isOptionEqualToValue={(o, v) => o._id === v._id}
            sx={{ mt: 1, mb: 0.5 }}
            renderInput={(params) => (
              <TextField {...params} label="Property" required margin="normal" fullWidth />
            )}
          />
          {formData.propertyId && selectedProperty && selectedProperty.propertyType === 'commercial' && (
            <>
              <TextField
                fullWidth
                label="Square Feet"
                type="number"
                margin="normal"
                required
                value={allocationData.squareFeet}
                onFocus={(e) => e.target.select()}
                onChange={(e) =>
                  setAllocationData({ ...allocationData, squareFeet: parseInt(e.target.value) || 0 })
                }
                helperText={`Available: ${(selectedProperty.availableSquareFeet ?? selectedProperty.squareFeet ?? 0).toLocaleString()} sq ft`}
                inputProps={{
                  max: selectedProperty.availableSquareFeet ?? selectedProperty.squareFeet ?? 0
                }}
              />
              <TextField
                fullWidth
                label="Monthly Rent"
                type="number"
                margin="normal"
                required
                value={allocationData.monthlyRent}
                onFocus={(e) => e.target.select()}
                onChange={(e) =>
                  setAllocationData({ ...allocationData, monthlyRent: parseFloat(e.target.value) || 0 })
                }
              />
              <TextField
                fullWidth
                label="Deposit"
                type="number"
                margin="normal"
                value={allocationData.deposit}
                onFocus={(e) => e.target.select()}
                onChange={(e) =>
                  setAllocationData({ ...allocationData, deposit: parseFloat(e.target.value) || 0 })
                }
              />
            </>
          )}
          {formData.propertyId && selectedProperty && selectedProperty.propertyType !== 'commercial' && (
            <Autocomplete
              options={units}
              value={units.find(u => u._id === formData.unitId) || null}
              getOptionLabel={(unit) => {
                const isCurrentlyAssigned = isReassigning && selectedTenant &&
                  (selectedTenant.unit?._id === unit._id || selectedTenant.unit === unit._id);
                return `${unit.unitNumber} - ${unit.bedrooms}BR/${unit.bathrooms}BA - ${formatMoney(unit.monthlyRent, { decimals: 0 })}${isCurrentlyAssigned ? ' (Current)' : unit.status === 'occupied' ? ` (Occupied: ${unit.assignedTenant?.name || 'Unknown'})` : ' (Available)'}`;
              }}
              getOptionDisabled={(unit) => {
                const isCurrentlyAssigned = isReassigning && selectedTenant &&
                  (selectedTenant.unit?._id === unit._id || selectedTenant.unit === unit._id);
                return !isReassigning && unit.status !== 'available' && !isCurrentlyAssigned;
              }}
              onChange={(_, val) => setFormData({ ...formData, unitId: val?._id || '' })}
              isOptionEqualToValue={(o, v) => o._id === v._id}
              sx={{ mt: 1, mb: 0.5 }}
              renderInput={(params) => (
                <TextField {...params} label="Unit" required margin="normal" fullWidth />
              )}
            />
          )}
          <TextField
            fullWidth
            label="Lease Start Date"
            type="date"
            margin="normal"
            value={formData.leaseStartDate}
            onChange={(e) =>
              setFormData({ ...formData, leaseStartDate: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Lease End Date (Optional)"
            type="date"
            margin="normal"
            value={formData.leaseEndDate}
            onChange={(e) =>
              setFormData({ ...formData, leaseEndDate: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Payment Date (Day of Month)"
            type="number"
            margin="normal"
            inputProps={{ min: 1, max: 31 }}
            value={formData.paymentDate}
            onChange={(e) =>
              setFormData({ ...formData, paymentDate: parseInt(e.target.value) || 1 })
            }
            helperText="Day of each month when rent is due (1-31)."
            required
          />
          <TextField
            fullWidth
            label="Billing Cycle Starts On (Day of Month)"
            type="number"
            margin="normal"
            inputProps={{ min: 1, max: 31 }}
            value={formData.billingCycleDay}
            onChange={(e) =>
              setFormData({ ...formData, billingCycleDay: parseInt(e.target.value) || 1 })
            }
            helperText="Day the tenant's rent month starts (1-31). Leave at 1 for the calendar month. A tenant who moved in on the 10th is billed 10th to 9th, and the invoice is raised on the 10th."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpen(false);
            setIsReassigning(false);
          }}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              selectedProperty?.propertyType === 'commercial'
                ? !allocationData.squareFeet || allocationData.squareFeet <= 0
                : !formData.unitId
            }
          >
            {isReassigning ? 'Reassign' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Commercial Allocation Dialog */}
      <Dialog open={allocationOpen} onClose={() => setAllocationOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Allocate Square Feet to Tenant</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={properties.filter(p => p.propertyType === 'commercial')}
            value={properties.find(p => p._id === allocationData.propertyId) || null}
            getOptionLabel={(p) => `${p.propertyName}${p.addressStreet ? ` - ${p.addressStreet}` : ''} (${(p.totalSquareFeet || p.squareFeet || 0).toLocaleString()} sq ft)`}
            onChange={(_, val) => {
              setAllocationData({ ...allocationData, propertyId: val?._id || '' });
              setSelectedProperty(val || null);
            }}
            isOptionEqualToValue={(o, v) => o._id === v._id}
            sx={{ mt: 1, mb: 0.5 }}
            renderInput={(params) => (
              <TextField {...params} label="Commercial Property" required margin="normal" fullWidth />
            )}
          />
          {allocationData.propertyId && selectedProperty && (
            <>
              <TextField
                fullWidth
                label="Square Feet"
                type="number"
                margin="normal"
                required
                value={allocationData.squareFeet}
                onChange={(e) =>
                  setAllocationData({ ...allocationData, squareFeet: parseInt(e.target.value) || 0 })
                }
                helperText={`Available: ${(selectedProperty.availableSquareFeet || selectedProperty.squareFeet || 0).toLocaleString()} sq ft`}
                inputProps={{ 
                  max: selectedProperty.availableSquareFeet || selectedProperty.squareFeet || 0 
                }}
              />
              <TextField
                fullWidth
                label="Monthly Rent"
                type="number"
                margin="normal"
                required
                value={allocationData.monthlyRent}
                onChange={(e) =>
                  setAllocationData({ ...allocationData, monthlyRent: parseFloat(e.target.value) || 0 })
                }
              />
              <TextField
                fullWidth
                label="Deposit"
                type="number"
                margin="normal"
                value={allocationData.deposit}
                onChange={(e) =>
                  setAllocationData({ ...allocationData, deposit: parseFloat(e.target.value) || 0 })
                }
              />
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                margin="normal"
                value={allocationData.startDate}
                onChange={(e) =>
                  setAllocationData({ ...allocationData, startDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="End Date (Optional)"
                type="date"
                margin="normal"
                value={allocationData.endDate}
                onChange={(e) =>
                  setAllocationData({ ...allocationData, endDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                margin="normal"
                value={allocationData.description}
                onChange={(e) =>
                  setAllocationData({ ...allocationData, description: e.target.value })
                }
              />
              <TextField
                fullWidth
                label="Payment Date (Day of Month)"
                type="number"
                margin="normal"
                inputProps={{ min: 1, max: 31 }}
                value={allocationData.paymentDate}
                onChange={(e) =>
                  setAllocationData({ ...allocationData, paymentDate: parseInt(e.target.value) || 1 })
                }
                helperText="Day of each month when rent is due (1-31)."
                required
              />
              <TextField
                fullWidth
                label="Billing Cycle Starts On (Day of Month)"
                type="number"
                margin="normal"
                inputProps={{ min: 1, max: 31 }}
                value={allocationData.billingCycleDay}
                onChange={(e) =>
                  setAllocationData({ ...allocationData, billingCycleDay: parseInt(e.target.value) || 1 })
                }
                helperText="Day the tenant's rent month starts (1-31). 1 means the calendar month."
                required
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAllocationOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!allocationData.propertyId || !allocationData.squareFeet}>
            {selectedTenant?.property ? 'Update' : 'Create'} Allocation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Tenant Dialog */}
      <Dialog open={addTenantOpen} onClose={() => setAddTenantOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Tenant</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                required
                value={newTenantData.name}
                onChange={(e) =>
                  setNewTenantData({ ...newTenantData, name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email (Optional)"
                type="email"
                value={newTenantData.email}
                onChange={(e) =>
                  setNewTenantData({ ...newTenantData, email: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone (Optional)"
                value={newTenantData.phone}
                onChange={(e) =>
                  setNewTenantData({ ...newTenantData, phone: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="WhatsApp Number (Optional)"
                helperText="Only if different from the phone number above"
                value={newTenantData.whatsappPhone}
                onChange={(e) =>
                  setNewTenantData({ ...newTenantData, whatsappPhone: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Payment Date (Day of Month)"
                type="number"
                inputProps={{ min: 1, max: 31 }}
                value={newTenantData.paymentDate}
                onChange={(e) =>
                  setNewTenantData({ ...newTenantData, paymentDate: parseInt(e.target.value) || 1 })
                }
                helperText="Day of each month when rent is due (1-31)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Billing Cycle Starts On (Day of Month)"
                type="number"
                inputProps={{ min: 1, max: 31 }}
                value={newTenantData.billingCycleDay}
                onChange={(e) =>
                  setNewTenantData({ ...newTenantData, billingCycleDay: parseInt(e.target.value) || 1 })
                }
                helperText="Day the rent month starts (1-31). Leave at 1 for the calendar month."
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newTenantData.assignUnit}
                    onChange={(e) =>
                      setNewTenantData({ ...newTenantData, assignUnit: e.target.checked })
                    }
                  />
                }
                label="Assign unit immediately"
              />
            </Grid>
            {newTenantData.assignUnit && (
              <>
                <Grid item xs={12}>
                  <Autocomplete
                    options={properties}
                    value={properties.find(p => p._id === newTenantData.propertyId) || null}
                    getOptionLabel={(p) => `${p.propertyName}${p.addressStreet ? ` - ${p.addressStreet}` : ''} (${p.propertyType})`}
                    onChange={(_, val) => handleNewTenantPropertyChange(val?._id || '')}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    renderInput={(params) => (
                      <TextField {...params} label="Property" fullWidth />
                    )}
                  />
                </Grid>
                {newTenantData.propertyId && selectedProperty && selectedProperty.propertyType === 'commercial' && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Square Feet"
                        type="number"
                        value={newTenantData.allocationSquareFeet}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) =>
                          setNewTenantData({ ...newTenantData, allocationSquareFeet: parseInt(e.target.value) || 0 })
                        }
                        helperText={`Available: ${(selectedProperty.availableSquareFeet ?? selectedProperty.squareFeet ?? 0).toLocaleString()} sq ft`}
                        inputProps={{
                          max: selectedProperty.availableSquareFeet ?? selectedProperty.squareFeet ?? 0
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Monthly Rent"
                        type="number"
                        value={newTenantData.allocationMonthlyRent}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) =>
                          setNewTenantData({ ...newTenantData, allocationMonthlyRent: parseFloat(e.target.value) || 0 })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Deposit"
                        type="number"
                        value={newTenantData.allocationDeposit}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) =>
                          setNewTenantData({ ...newTenantData, allocationDeposit: parseFloat(e.target.value) || 0 })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Lease Start Date"
                        type="date"
                        value={newTenantData.leaseStartDate}
                        onChange={(e) =>
                          setNewTenantData({ ...newTenantData, leaseStartDate: e.target.value })
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Lease End Date (Optional)"
                        type="date"
                        value={newTenantData.leaseEndDate}
                        onChange={(e) =>
                          setNewTenantData({ ...newTenantData, leaseEndDate: e.target.value })
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </>
                )}
                {newTenantData.propertyId && selectedProperty && selectedProperty.propertyType !== 'commercial' && (
                  <>
                    <Grid item xs={12}>
                      <Autocomplete
                        options={units.filter(u => u.status === 'available')}
                        value={units.find(u => u._id === newTenantData.unitId) || null}
                        getOptionLabel={(u) => `${u.unitNumber} - ${u.bedrooms}BR/${u.bathrooms}BA - ${u.squareFeet} sq ft - ${formatMoney(u.monthlyRent, { decimals: 0 })}`}
                        onChange={(_, val) => setNewTenantData({ ...newTenantData, unitId: val?._id || '' })}
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        noOptionsText="No available units"
                        renderInput={(params) => (
                          <TextField {...params} label="Unit" fullWidth />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Lease Start Date"
                        type="date"
                        value={newTenantData.leaseStartDate}
                        onChange={(e) =>
                          setNewTenantData({ ...newTenantData, leaseStartDate: e.target.value })
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Lease End Date (Optional)"
                        type="date"
                        value={newTenantData.leaseEndDate}
                        onChange={(e) =>
                          setNewTenantData({ ...newTenantData, leaseEndDate: e.target.value })
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTenantOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateTenant} 
            variant="contained"
            disabled={!newTenantData.name}
          >
            Create Tenant
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Contact Details Dialog */}
      <Dialog open={editContactOpen} onClose={() => setEditContactOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Tenant</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={contactData.name}
                onChange={(e) => setContactData({ ...contactData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={contactData.email}
                onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={contactData.phone}
                onChange={(e) => setContactData({ ...contactData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="WhatsApp Number"
                helperText="Leave blank to use the phone number"
                value={contactData.whatsappPhone}
                onChange={(e) => setContactData({ ...contactData, whatsappPhone: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">Tenancy</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth type="number" label="Rent Day"
                inputProps={{ min: 1, max: 31 }}
                value={contactData.paymentDate}
                onChange={(e) => setContactData({ ...contactData, paymentDate: e.target.value })}
                helperText="Day of the month rent falls due"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth type="number" label="Billing Cycle Starts On"
                inputProps={{ min: 1, max: 31 }}
                value={contactData.billingCycleDay}
                onChange={(e) => setContactData({ ...contactData, billingCycleDay: e.target.value })}
                helperText="Day the rent month starts. 10 bills 10th to 9th; leave at 1 for the calendar month"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth type="date" label="Lease Start"
                InputLabelProps={{ shrink: true }}
                value={contactData.leaseStartDate}
                onChange={(e) => setContactData({ ...contactData, leaseStartDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth type="date" label="Lease End"
                InputLabelProps={{ shrink: true }}
                value={contactData.leaseEndDate}
                onChange={(e) => setContactData({ ...contactData, leaseEndDate: e.target.value })}
                helperText="Blank if open-ended"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">Emergency Contact</Typography>
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth label="Name"
                value={contactData.emergencyContactName}
                onChange={(e) => setContactData({ ...contactData, emergencyContactName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Phone"
                value={contactData.emergencyContactPhone}
                onChange={(e) => setContactData({ ...contactData, emergencyContactPhone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth label="Relationship"
                value={contactData.emergencyContactRelation}
                onChange={(e) => setContactData({ ...contactData, emergencyContactRelation: e.target.value })}
              />
            </Grid>

            {/* Said here because this dialog is where someone will look for it. */}
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Property and unit are changed with Assign / Reassign, not here.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditContactOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveContact} variant="contained" disabled={!contactData.name}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ledger Dialog */}
      <Dialog 
        open={viewLedger} 
        onClose={() => {
          setViewLedger(false);
          setSelectedTenant(null);
        }} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          {selectedTenant?.name} - Ledger
        </DialogTitle>
        <DialogContent>
          {selectedTenant && (
            <TenantLedger 
              tenantId={selectedTenant._id} 
              tenantName={selectedTenant.name}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setViewLedger(false);
            setSelectedTenant(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <TenantTransferDialog
        open={!!transferTenant}
        tenant={transferTenant}
        onClose={() => setTransferTenant(null)}
        onDone={() => {
          fetchTenants();
          fetchProperties();
        }}
      />
    </Paper>
  );
};

export default TenantManagement;

